import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Target, MessageSquare, Lightbulb, ChevronRight, Zap, AlertCircle,
} from "lucide-react";

export function SalesPlaybook({ playbook }) {
  if (!playbook) return null;
  return (
    <Card data-testid="sales-playbook" className="border-indigo-200 shadow-sm bg-gradient-to-br from-indigo-50/50 to-slate-50">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center"><Target className="w-4 h-4 text-white" /></div>
          <div>
            <h2 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Sales Playbook</h2>
            <p className="text-[10px] text-muted-foreground">Your recommended approach for this prospect</p>
          </div>
          <Badge className="ml-auto text-[10px] bg-indigo-100 text-indigo-700 border-indigo-200 h-5" variant="outline">{playbook.lead_angle}</Badge>
        </div>

        {/* Opener */}
        <div className="p-3 rounded-lg bg-white border border-indigo-100 mb-3">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-0.5">How to open the conversation</p>
              <p className="text-[12px] text-slate-700 leading-relaxed">{playbook.opener}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {/* Talking Points */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Key Talking Points</p>
            {playbook.talking_points?.map((tp, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                <ChevronRight className="w-3 h-3 text-indigo-400 mt-0.5 shrink-0" />
                <span>{tp}</span>
              </div>
            ))}
          </div>

          {/* Services */}
          <div className="space-y-2" data-testid="suggested-services">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Zap className="w-3 h-3" /> Suggested Services</p>
            {playbook.suggested_services?.map((svc, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`service-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-slate-800">{svc.name}</span>
                  <Badge variant="outline" className={`text-[8px] h-3.5 shrink-0 ${
                    svc.priority === "high" ? "bg-rose-50 text-rose-600 border-rose-200" :
                    svc.priority === "medium" ? "bg-amber-50 text-amber-600 border-amber-200" :
                    "bg-slate-50 text-slate-500 border-slate-200"
                  }`}>{svc.priority}</Badge>
                </div>
                {svc.category && <p className="text-[9px] uppercase tracking-wide text-indigo-500 font-medium mt-0.5">{svc.category}</p>}
                {svc.reason && <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{svc.reason}</p>}
              </div>
            ))}
            <p className="text-[9px] text-slate-400 italic pt-0.5">Preliminary direction — your AE will build the full strategy & quote.</p>
          </div>

          {/* Urgency */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Why Now</p>
            {playbook.urgency_signals?.map((u, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
                <span>{u}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
